import React, { useEffect } from "react";
import { Form, FormStepper } from "../../../components/common/Forms";
import Notifications from "../../../components/common/Notifications";
import UploadErrorBanner from "../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import {
  CitizenshipFormData,
  citizenshipStepFields,
  citizenshipStepTitles,
  citizenshipStepContents,
} from "../../../config/citizenshipFormConfig";
import { FieldConfig } from "../../../types/formTypes";
import { useAppDispatch } from "../../../store/hooks";
import { clearSavePatchError } from "../../../store/slices/Citizenship/citizenshipFormSlice";

interface ApplyFormProps {
  initialValues: CitizenshipFormData;
  showSuccess: boolean;
  applicationId?: string;
  error: string | null;
  onCloseSuccess: () => void;
  handleSubmit: (formData: CitizenshipFormData) => Promise<void>;
  handleSave: (values: Partial<CitizenshipFormData>) => Promise<void>;
  savePatchError?: string | null;
}

const ApplyForm: React.FC<ApplyFormProps> = ({
  initialValues,
  showSuccess,
  applicationId,
  error,
  onCloseSuccess,
  handleSubmit,
  handleSave,
  savePatchError,
}) => {
  const dispatch = useAppDispatch();
  // Auto-hide citizenship save patch error after 20s
  useEffect(() => {
    if (!savePatchError) return;
    const t = setTimeout(() => dispatch(clearSavePatchError()), 5000);
    return () => clearTimeout(t);
  }, [savePatchError, dispatch]);
  const renderStepForm = (
    stepFields: FieldConfig[],
    stepTitle: string,
    buttons: string[],
    onNext?: () => void,
    onBack?: () => void,
    onClear?: () => void,
    currentStep?: number
  ) => {
    if (showSuccess) return null;
    return (
      <Form<CitizenshipFormData>
        fields={stepFields}
        title={stepTitle}
        buttons={buttons}
        initialValues={initialValues}
        onFormSubmit={handleSubmit}
        onSave={handleSave}
        onNext={onNext}
        onBack={onBack}
        onClear={onClear}
        stepContent={
          typeof currentStep === "number"
            ? citizenshipStepContents[
                `step${currentStep}` as keyof typeof citizenshipStepContents
              ]
            : undefined
        }
        currentStep={currentStep}
        isApplicantInfoStep={currentStep === 1}
        className="min-[992px]:!w-[calc(100vw-40vw)]"
        formSpacing="!mt-[36px]"
      />
    );
  };

  const renderSuccess = () => (
    <div className="min-h-[50vh]!">
      <Notifications
        messageInfo={`Congratulations, you have successfully submitted your Citizenship Fee Assistance application is ${applicationId ? ` ${applicationId}` : ''}. Your next step is to upload supporting documents such as your N400 filing fee receipt.`}
        status={true}
      />
      <button
        className="button -primary -light w-full mt-21!"
        onClick={onCloseSuccess}
      >
        Close
      </button>
    </div>
  );

  return (
    <div
      className="mobile-stepper flex flex-col xl:flex-row xl:flex-nowrap sm:m-5!"
      data-testid="apply-form"
    >
      {error && !savePatchError && !showSuccess ? (
        <div className="min-h-[50vh] flex items-center justify-center w-full">
          <Notifications messageInfo={error} status={false} />
        </div>
      ) : (
        <FormStepper
          stepFields={citizenshipStepFields}
          stepTitles={citizenshipStepTitles}
          totalSteps={4}
          renderForm={renderStepForm}
          showSuccess={showSuccess}
          successContent={renderSuccess()}
          patchErrorBanner={savePatchError ? <UploadErrorBanner header="Error" message={savePatchError} /> : null}
        />
      )}
    </div>
  );
};

export default ApplyForm;